import { useState } from 'react';
import { format, parseISO, subDays, subMonths, subYears } from 'date-fns';
import { IconCalendarEvent } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@/components/ui/popover';
import { cn, formatDateOnly } from '@/lib/utils';
import * as m from '@/paraglide/messages.js';

interface DatePickerProps {
  id: string;
  label: string;
  value: string;
  onValueChange(value: string): void;
  onBlur?: (() => void) | undefined;
  today?: string | undefined;
  onToday?: (() => void) | undefined;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean | undefined;
  clearable?: boolean;
  clearLabel?: string;
  size?: 'default' | 'sm' | 'xl';
  appearance?: 'default' | 'field';
  invalid?: boolean;
  describedBy?: string | undefined;
  className?: string;
}

// Adapted from ReUI c-calendar-26/28 for Base UI and date-only values.
export function DatePicker({
  id,
  label,
  value,
  onValueChange,
  onBlur,
  today,
  onToday,
  min,
  max,
  disabled,
  clearable = false,
  clearLabel,
  size = 'default',
  appearance = 'default',
  invalid = false,
  describedBy,
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>();
  const base = today ? parseISO(today) : new Date();
  const selected = value ? parseISO(value) : undefined;
  const presets = [
    { label: m.common_today(), date: base, today: true },
    { label: m.date_picker_yesterday(), date: subDays(base, 1) },
    { label: m.date_picker_last_week(), date: subDays(base, 7) },
    { label: m.date_picker_last_month(), date: subMonths(base, 1) },
    { label: m.date_picker_last_year(), date: subYears(base, 1) },
  ];
  const isDisabled = (date: string) => !!((min && date < min) || (max && date > max));

  function select(date: Date) {
    onValueChange(format(date, 'yyyy-MM-dd'));
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setMonth(selected ?? base);
        setOpen(next);
      }}
    >
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant={appearance === 'field' ? 'input' : 'outline'}
            size={appearance === 'field' ? 'field' : size}
            disabled={disabled}
            onBlur={onBlur}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            aria-label={`${label}: ${value ? formatDateOnly(value) : m.date_picker_choose()}`}
            treatment="wrap"
            className={cn('w-40 min-w-0 max-w-full justify-between', className)}
          />
        }
      >
        <span
          className={cn(
            'min-w-0 whitespace-normal tabular-nums',
            !value && 'text-muted-foreground',
          )}
        >
          {value ? formatDateOnly(value) : m.date_picker_choose()}
        </span>
        <IconCalendarEvent data-icon="inline-end" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        size="flush"
        centerOnOverflow
        className="max-h-[min(calc(100dvh-2rem),var(--popover-viewport-height,100dvh))] w-auto max-w-[calc(100vw-2rem)] overflow-auto"
      >
        <PopoverTitle className="sr-only">{label}</PopoverTitle>
        <div className="flex flex-col sm:flex-row">
          <div className="flex flex-wrap gap-1 border-b border-border p-2 sm:w-36 sm:flex-col sm:border-r sm:border-b-0">
            {presets.map((preset) => {
              const date = format(preset.date, 'yyyy-MM-dd');
              return (
                <Button
                  key={preset.label}
                  type="button"
                  variant={date === value ? 'secondary' : 'ghost'}
                  size="xl"
                  className="justify-start"
                  aria-pressed={date === value}
                  disabled={isDisabled(date)}
                  onClick={() => {
                    if (preset.today && onToday) {
                      onToday();
                      setOpen(false);
                    } else select(preset.date);
                  }}
                >
                  {preset.label}
                </Button>
              );
            })}
          </div>
          <Calendar
            mode="single"
            fixedWeeks
            required
            autoFocus
            captionLayout="dropdown"
            selected={selected}
            month={month ?? selected ?? base}
            onMonthChange={setMonth}
            onSelect={select}
            startMonth={
              min
                ? parseISO(min)
                : new Date(
                    Math.min(base.getFullYear(), selected?.getFullYear() ?? base.getFullYear()) -
                      100,
                    0,
                  )
            }
            endMonth={
              max
                ? parseISO(max)
                : new Date(
                    Math.max(base.getFullYear(), selected?.getFullYear() ?? base.getFullYear()) +
                      20,
                    11,
                  )
            }
            disabled={(date) => isDisabled(format(date, 'yyyy-MM-dd'))}
            className="[--cell-size:var(--control-xl)]"
          />
        </div>
        {clearable && value && (
          <div className="border-t border-border p-2">
            <Button
              type="button"
              variant="ghost"
              size="xl"
              className="w-full"
              onClick={() => {
                onValueChange('');
                setOpen(false);
              }}
            >
              {clearLabel ?? m.date_picker_clear()}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
